import { DetailResponse } from '../../../model/work_response';
import { DataTypeCode } from '../../../constant';

/**
 * 詳細情報型
 */
type DetailRowFunc = (detail: DetailResponse) => TaskDetailRow[];
type DetailValueFunc = (value: string) => string;
type TaskDetailKeyValue = { key: string; name: string, type: 'normal' | 'list' | DetailValueFunc, rowFunc?: DetailRowFunc };
export type TaskDetailRow = { key: string, name: string, value?: string, span?: boolean, child?: boolean };

/**
 * 詳細情報
 * @param details
 */
const createBispotShipingDetails = (details: DetailResponse): TaskDetailRow[] => {
  type ShippingDetail = { name: string, carry: string, dock_usage: string, category: string, datetime: string, vehicle: string };
  const shippings = details.shipping_details;
  if (shippings === undefined || shippings === null || typeof shippings !== 'object') {
    return [];
  }
  const names = ['①', '②', '③', '④', '⑤', '⑥'];
  return Object.entries(shippings).flatMap(([key, value]): TaskDetailRow[] => {
    const idx = parseInt(key, 10);
    const shipping = value as ShippingDetail;
    if (Number.isNaN(idx) || idx < 0 || idx >= names.length) {
      return [];
    }
    const name = names[idx];
    if ((shipping.carry + shipping.dock_usage + shipping.category + shipping.datetime + shipping.vehicle).length === 0) {
      return [];
    }
    return [
      { key: `shipping_${idx}_name`, name: '搬出入詳細' + name, span: true },
      { key: `shipping_${idx}_carry`, name: '館内キャリー確認欄' + name, value: shipping.carry, child: true },
      { key: `shipping_${idx}_dock_usage`, name: '使用する荷捌き' + name, value: shipping.dock_usage, child: true },
      { key: `shipping_${idx}_category`, name: '搬出入区分' + name, value: shipping.category, child: true },
      { key: `shipping_${idx}_datetime`, name: '搬出入日時' + name, value: shipping.datetime, child: true },
      { key: `shipping_${idx}_vehicle`, name: '車両' + name, value: shipping.vehicle, child: true },
    ];
  });
};

/**
 * 優先度ステータス
 * @param value
 */
const priorityString: DetailValueFunc = (value: string): string => {
  switch (value) {
    case 'low':
      return '低';
    case 'medium':
      return '中';
    case 'high':
      return '高';
    default:
      return '不明';
  }
};

/**
 * 業務データ種別
 * @param value
 */
const businessTypeString: DetailValueFunc = (value: string): string => {
  switch (value) {
    case 'cleaning':
      return '清掃';
    case 'security':
      return '警備';
    case 'equipment':
      return '設備';
    case 'greening':
      return '植栽';
    case 'bispot':
      return 'Bispot';
    case 'none':
      return '種別なし';
    case 'all':
      return 'すべて';
    default:
      return '不明';
  }
};

/**
 * ステータス種別
 * @param value
 */
const statusString: DetailValueFunc = (value: string): string => {
  switch (value) {
    case 'incomplete':
      return '未対応';
    case 'inprogress':
      return '対応中';
    case 'completed':
      return '対応終了';
    case 'none':
      return '不明';
    default:
      return '不明';
  }
};

/**
 * 列名一覧
 */
const TaskDetails: { [key: string]: TaskDetailKeyValue[] } = {
  // Bispot作業申請情報
  bispot: [
    { key: 'bispot_id', name: '届け出No', type: 'normal' },
    { key: 'report_name', name: '届出帳票名', type: 'normal' },
    { key: 'work_subject', name: '件名', type: 'normal' },
    { key: 'start_datetime', name: '実施日（開始）', type: 'normal' },
    { key: 'end_datetime', name: '実施日（終了）', type: 'normal' },
    { key: 'application_date', name: '申請日', type: 'normal' },
    { key: 'company', name: '入居者', type: 'normal' },
    { key: 'applicant', name: '申請者', type: 'normal' },
    { key: 'status', name: 'ステータス', type: 'normal' },
    { key: 'in_company', name: 'ご入居会社名', type: 'normal' },
    { key: 'applicant_name', name: 'ご担当者名（申請者）', type: 'normal' },
    { key: 'tel', name: '連絡先（TEL）', type: 'normal' },
    { key: 'email', name: '連絡先（E-mail）', type: 'normal' },
    { key: 'work_period', name: '作業期間（開始日～終了日）', type: 'normal' },
    { key: 'work_hours', name: '実作業時間', type: 'normal' },
    { key: 'work_company', name: '作業会社名', type: 'normal' },
    { key: 'work_tel', name: '作業会社：連絡先（TEL）', type: 'normal' },
    { key: 'site_manager', name: '現場責任者名', type: 'normal' },
    { key: 'site_tel', name: '現場責任者：連絡先（TEL）', type: 'normal' },
    { key: 'number_of_visitors', name: '入館者数（●名）', type: 'normal' },
    { key: 'worker_roster', name: '作業員名簿', type: 'normal' },
    { key: 'building', name: 'ビル名', type: 'normal' },
    { key: 'building_of_work', name: '作業する棟', type: 'normal' },
    { key: 'target', name: '作業階/場所', type: 'normal' },
    { key: 'task_summary', name: '作業内容', type: 'normal' },
    { key: 'shipping_presence', name: '搬出入の有無', type: 'normal' },
    { key: 'shipping_details', name: '搬出入詳細', type: 'list', rowFunc: createBispotShipingDetails },
    { key: 'special', name: '特殊作業の内容', type: 'normal' },
    { key: 'other_details', name: 'その他（内容）', type: 'normal' },
    { key: 'fire', name: '火気・危険物使用', type: 'normal' },
    { key: 'special_work_datetime', name: '特殊作業の実施日時', type: 'normal' },
    { key: 'keys', name: '鍵貸出・セキュリティ開錠依頼', type: 'normal' },
    { key: 'unlock_location', name: '開錠依頼場所', type: 'normal' },
    { key: 'special_notes', name: '特記事項', type: 'normal' },
    { key: 'accepted_day', name: '承認日', type: 'normal' },
    { key: 'manager_note', name: '管理者記載欄', type: 'normal' },
    { key: 'curing', name: '養生の要否', type: 'normal' },
    { key: 'armband_loan', name: '腕章の貸出し有無', type: 'normal' },
    { key: 'armband_loan_location', name: '腕章の貸出し場所', type: 'normal' },
    { key: 'security_card', name: 'セキュリティカード貸出し有無', type: 'normal' },
    { key: 'card_no', name: '貸出する鍵番号・カード', type: 'normal' },
  ],
  // BT-SAS作業計画・実績
  btsas: [
    { key: 'facility', name: '施設', type: 'normal' },
    { key: 'building', name: '建物', type: 'normal' },
    { key: 'division', name: '棟', type: 'normal' },
    { key: 'floor', name: '階', type: 'normal' },
    { key: 'block', name: '場所', type: 'normal' },
    { key: 'place_info', name: '場所詳細', type: 'normal' },
    { key: 'task_summary', name: '内容区分', type: 'normal' },
    { key: 'inspection_type', name: '区分・設備', type: 'normal' },
    { key: 'target', name: '項目', type: 'normal' },
    { key: 'frequency', name: '周期', type: 'normal' },
    { key: 'partner_company', name: '協力会社', type: 'normal' },
    { key: 'reason', name: '繰越・前倒し理由', type: 'normal' },
  ],
  // 日常作業計画(清掃)
  cleaning_daily: [
    { key: 'block', name: '【区画】', type: 'normal' },
    { key: 'manage_type', name: '管理区分', type: 'normal' },
    { key: 'manage_type_id', name: '管理区分毎ID', type: 'normal' },
    { key: 'type_1', name: '区分-1', type: 'normal' },
    { key: 'division', name: '区分-2', type: 'normal' },
    { key: 'floor', name: '階', type: 'normal' },
    { key: 'target', name: '室(部位)名', type: 'normal' },
    { key: 'material', name: '素材', type: 'normal' },
    { key: 'memo', name: '備考', type: 'normal' },
    { key: 'quantity', name: '数量', type: 'normal' },
    { key: 'unit', name: '単位', type: 'normal' },
    { key: 'section', name: '作業区分コード(A1,A2)', type: 'normal' },
    { key: 'section_name', name: '作業区分名称', type: 'normal' },
    { key: 'work_company', name: '担当会社', type: 'normal' },
    { key: 'frequency', name: '頻度', type: 'normal' },
  ],
  // 定期作業計画(清掃)
  cleaning_reg: [
    { key: 'block', name: '【区画】', type: 'normal' },
    { key: 'manage_type', name: '管理区分', type: 'normal' },
    { key: 'manage_type_id', name: '管理区分毎ID', type: 'normal' },
    { key: 'type_1', name: '区分-1', type: 'normal' },
    { key: 'division', name: '区分-2', type: 'normal' },
    { key: 'floor', name: '階層', type: 'normal' },
    { key: 'floor_no', name: 'FL毎No.', type: 'normal' },
    { key: 'target', name: '対象箇所', type: 'normal' },
    { key: 'material', name: '材質', type: 'normal' },
    { key: 'square_size', name: '面積', type: 'normal' },
    { key: 'regular_cleaning_yearly_frequency', name: '年間回数(頻度)', type: 'normal' },
    { key: 'task_summary', name: '作業内容', type: 'normal' },
    { key: 'fulfill_confirmer', name: '履行確認者', type: 'normal' },
  ],
  // 常駐作業計画(植栽)
  greening_daily: [
    { key: 'block', name: '【区画】', type: 'normal' },
    { key: 'division', name: '街区', type: 'normal' },
    { key: 'floor', name: '階', type: 'normal' },
    { key: 'target', name: '箇所', type: 'normal' },
    { key: 'type_of_work', name: '工種', type: 'normal' },
    { key: 'task_summary', name: '仕様（作業項目）', type: 'normal' },
    { key: 'start_time', name: '開始時間', type: 'normal' },
    { key: 'end_time', name: '終了時間', type: 'normal' },
    { key: 'shared_with', name: '共有形態　①共有先', type: 'normal' },
    { key: 'shared_content', name: '共有形態　②内容', type: 'normal' },
    { key: 'shared_position', name: '共有形態　③位置', type: 'normal' },
    { key: 'shared_other', name: '共有形態　④その他', type: 'normal' },
    { key: 'shared_spare1', name: '予備　①', type: 'normal' },
    { key: 'shared_spare2', name: '予備　②', type: 'normal' },
    { key: 'shared_spare3', name: '予備　③', type: 'normal' },
    { key: 'shared_spare4', name: '予備　④', type: 'normal' },
    { key: 'shared_spare5', name: '予備　⑤', type: 'normal' },
    { key: 'shared_spare6', name: '予備　⑥', type: 'normal' },
    { key: 'memo', name: '実施時期・備考', type: 'normal' },
  ],
  // 定期作業計画(植栽)
  greening_reg: [
    { key: 'block', name: '【区画】', type: 'normal' },
    { key: 'division', name: '街区', type: 'normal' },
    { key: 'floor', name: '階', type: 'normal' },
    { key: 'target', name: '箇所', type: 'normal' },
    { key: 'type_of_work', name: '工種', type: 'normal' },
    { key: 'task_summary', name: '仕様（作業項目）', type: 'normal' },
    { key: 'start_time', name: '開始時間', type: 'normal' },
    { key: 'end_time', name: '終了時間', type: 'normal' },
    { key: 'shared_with', name: '共有形態　①共有先', type: 'normal' },
    { key: 'shared_content', name: '共有形態　②内容', type: 'normal' },
    { key: 'shared_position', name: '共有形態　③位置', type: 'normal' },
    { key: 'shared_other', name: '共有形態　④その他', type: 'normal' },
    { key: 'shared_spare1', name: '予備　①', type: 'normal' },
    { key: 'shared_spare2', name: '予備　②', type: 'normal' },
    { key: 'shared_spare3', name: '予備　③', type: 'normal' },
    { key: 'shared_spare4', name: '予備　④', type: 'normal' },
    { key: 'shared_spare5', name: '予備　⑤', type: 'normal' },
    { key: 'shared_spare6', name: '予備　⑥', type: 'normal' },
    { key: 'memo', name: '実施時期・備考', type: 'normal' },
    { key: 'fulfill_confirmer', name: '履行確認者', type: 'normal' },
  ],
  // 臨時作業依頼
  request: [
    { key: 'business_type', name: '担当業務', type: businessTypeString },
    { key: 'work_company', name: '担当会社', type: 'normal' },
    { key: 'issue_type_id', name: '発生事象ID', type: 'normal' },
    { key: 'issue_type', name: '発生事象', type: 'normal' },
    { key: 'issue_type_other', name: '発生事象(その他)', type: 'normal' },
    { key: 'task_summary', name: '依頼内容', type: 'normal' },
    { key: 'supported_status', name: '対応状況', type: statusString },
    { key: 'issue_date', name: '発生日', type: 'normal' },
    { key: 'issue_time', name: '発生時間', type: 'normal' },
    { key: 'issue_time_str', name: '発生時間(文字列)', type: 'normal' },
    { key: 'target', name: '対象箇所', type: 'normal' },
    { key: 'urgency', name: '緊急度', type: priorityString },
    { key: 'priority', name: '優先度', type: priorityString },
    { key: 'expired_date', name: '対応期限日', type: 'normal' },
    { key: 'expired_time', name: '対応期限時間', type: 'normal' },
    { key: 'process_detailcep', name: '処置内容', type: 'normal' },
    { key: 'assigner', name: '担当者', type: 'normal' },
    { key: 'memo', name: '備考', type: 'normal' },
    { key: 'receptionist', name: '受付者', type: 'normal' },
    { key: 'issuedFrom', name: '依頼元', type: 'normal' },
    { key: 'issuer', name: '依頼者', type: 'normal' },
    { key: 'issue_company', name: '起票会社', type: 'normal' },
    { key: 'issue_user', name: '起票者', type: 'normal' },
  ],
};

/**
 * 詳細情報の作成
 * @param data
 * @param dataType
 */
export function createDetails(data: DetailResponse, dataType: DataTypeCode): TaskDetailRow[] {
  const details = TaskDetails[dataType];
  if (details === undefined || details === null || !Array.isArray(details)) {
    return [];
  }
  return details.flatMap((detail: TaskDetailKeyValue): TaskDetailRow[] => {
    if (detail.type === 'normal') {
      const value = data[detail.key];
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      return [{ key: detail.key, name: detail.name, value: value?.toString() ?? '' }];
    }
    if (typeof detail.type === 'function') {
      console.log('detail.type is function');
      const value = data[detail.key];
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      return [{ key: detail.key, name: detail.name, value: detail.type(value?.toString() ?? '') }];
    }
    if (detail.type === 'list' && detail.rowFunc !== undefined) {
      return detail.rowFunc(data);
    }
    return [];
  });
}
